import React from "react";
import { useTranslation } from "react-i18next";
import { Heart } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFavoriteToggle } from "../hooks/use-favorite-toggle";

interface FavoriteButtonProps {
  /** Product ObjectId. Yoksa buton çizilmez — kalpsiz kart, yanlış kalpten iyi. */
  productId: string | null | undefined;
  /** Konumlandırma çağıranın işi (kartta absolute, detayda satır içi). */
  className?: string;
  iconClassName?: string;
  size?: number;
  /**
   * Masaüstünde kalp yalnızca hover'da belirsin. Telefonda hover yok —
   * orada kalp her zaman görünür, aksi halde dokunmatikte hiç açılmayan bir
   * buton olurdu. Favorilenmiş ürünün kalbi de her boyutta görünür kalır,
   * yoksa kart kendi durumunu saklar.
   */
  hideUntilHover?: boolean;
}

/**
 * Kalp butonu.
 *
 * Durumunu `useFavoriteToggle`'dan alır; çağıran yalnızca hangi ürün
 * olduğunu ve nereye konumlanacağını söyler. Kart bir `<Link>` olduğu için
 * tıklama yayılımı burada durdurulur, aksi halde kalbe basınca ürün açılırdı.
 */
export const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  productId,
  className,
  iconClassName,
  size = 14,
  hideUntilHover = false,
}) => {
  const { t } = useTranslation();
  const { isFavorite, isPending, toggle } = useFavoriteToggle(productId);

  if (!productId) return null;

  return (
    <button
      type="button"
      aria-pressed={isFavorite}
      aria-label={
        isFavorite ? t("favorites.remove") : t("productDetail.addToFavorites")
      }
      title={
        isFavorite ? t("favorites.remove") : t("productDetail.addToFavorites")
      }
      disabled={isPending}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void toggle();
      }}
      className={cn(
        "flex items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60",
        hideUntilHover &&
          !isFavorite &&
          "transition-opacity duration-200 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100",
        className,
      )}
    >
      <Heart
        size={size}
        className={cn(
          "transition-colors",
          isFavorite
            ? "fill-current text-module-accent dark:text-module-accent"
            : "text-gray-700 dark:text-gray-300",
          iconClassName,
        )}
      />
    </button>
  );
};
